import { ElementRef, SimpleChanges, ViewChild, AfterViewInit, OnChanges, OnDestroy, OnInit, ChangeDetectorRef } from "@angular/core";
import { Component, Input, Output, EventEmitter } from "@angular/core";
import { AngularFireDatabase } from "@angular/fire/compat/database";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Observable } from "rxjs";
import { Sort, MatSort } from "@angular/material/sort";
import { ProjectService } from "src/app/Services/project.service";
import { ConfirmationDialogComponent } from "src/app/confirmation-dialog/confirmation-dialog.component";
import { SharedService } from "src/app/Services/sharedService";
import { ScreencastActionModelComponent } from "src/app/screencast/screencast-action-model/screencast-action-model.component";
import { Router, ActivatedRoute } from "@angular/router";
import { CustomTimeAgoPipe } from '../../time-ago.pipe';
import { NewPasswordComponent } from "src/app/new-password/new-password.component";
import { PasswordResetsDialogComponent } from "src/app/components/password-resets-dialog/password-resets-dialog.component";
import { colors } from "src/app/colors";
import swal from "sweetalert2";
import { AdminControlConfirmDialogComponent } from '../create-user/admin-control-confirm-dialog/admin-control-confirm-dialog.component';
import { SweetAlertService } from 'src/app/Services/sweet-alert.service';
import { environment } from "src/environments/environment";
import { sysConfig } from "src/app/sysConfig";
import {
  installerConfirmCopy,
  resolveEmployeeFirstName,
  isInstallerFirstTimeEmployee,
} from "src/app/utils/installer-confirm-copy.util";
import { ModalService } from "src/app/Services/modal.service";
import { CreateUserComponent } from "../create-user/create-user.component";
import { DummyDataService } from "src/app/Services/dummy-data.service";
import { TokenEncryptionService } from "src/app/Services/token-encryption.service";
import moment from "moment";
import { ScreenshotTimeService } from "src/app/Services/screenshot-time.service";
import { EntitlementsService } from "src/app/Services/entitlements.service";
import { ActivationJourneyService } from "src/app/onboarding-checklist/activation-journey.service";
import { EntitlementKey } from "src/app/entitlements/entitlement-keys";
import {
  getSoftwareTypeOptions,
  SoftwareTypeOption,
  SoftwareVersionType,
  validateSoftwareVersionType,
} from "src/app/entitlements/plan-software-type.util";
import { PlanUpgradeDialogComponent } from "src/app/components/plan-upgrade-dialog/plan-upgrade-dialog.component";
import { getEmployeeTrackingMode } from "src/app/entitlements/employee-tracking-mode.util";
import { Subscription } from "rxjs";
import { getEmployeeListStatusType, getSignedOutLabelForUser, getPasswordResetLabelForUser } from "src/app/utils/employee-status.util";
import {
  isEmployeesListActivityQuiet,
  isLiveActivityStatus,
  STALE_LIVE_SCREENSHOT_MS,
} from "src/app/utils/employees-list-activity.util";
import {
  formatScreenshotCell,
  getLastSeenMs,
  getPresenceCaption,
  getPresenceTooltip,
  parsePresenceTimeMs,
} from "src/app/utils/employees-list-presence.util";
import { trackClarityEvent } from "src/app/utils/clarity-event.util";

@Component({
  selector: "app-users-table",
  templateUrl: "./users-table.component.html",
  styleUrls: ["./users-table.component.css"],
  standalone: false
})
export class UsersTableComponent implements AfterViewInit, OnChanges, OnInit, OnDestroy {
  usersLists: Array<any> = [];
  filteredUserList: Array<any> = [];
  isOpenFirstTimeModal: boolean = false;
  currentSelectedFilterStatus: any;
  private hasCheckedInitialModal: boolean = false;
  @Input('isHideTeams') isHideTeams: boolean;
  @Input()
  get developerList() {
    return this.usersLists;
  }
  set developerList(value: Array<any>) {
    // Detect known dummy list (e.g., User1..User5 without runtime status)
    const isDummyList = Array.isArray(value) && value.length > 0 && value.every(u => typeof u?.name === 'string' && /^User\d+$/i.test(u.name));
    this.isDummyMode = isDummyList;

    // Emit the demo data state to the global service for banner visibility
    this._dummyDataService.setDemoDataVisible(isDummyList);

    this.usersLists = isDummyList
      ? value.map(u => this.withDemoMeta({ ...u, id: u.id || u.name, status: u.status || 'online' }))
      : value;
    this.filteredUserList = this.usersLists;

    // Start/stop demo random toasts depending on whether the dummy list is showing
    // Disabled by default; flip `enableDemoRandomToasts` to true to re-enable.
    if (isDummyList && this.enableDemoRandomToasts) {
      this.rebuildDemoUserQueue();
      this.startDemoToasts();
    } else {
      this.stopDemoToasts();
    }
  }

  @Input() devList: any;
  @Input() modalRef: ElementRef;
  @Input() widthStyle: any;
  @Input() isMaxContent: string;
  @Input() loadWaiter: Boolean;
  @Input() statusFilter: { label: string, value: string, class: string };
  @Output() deleteEmployeee = new EventEmitter<any>();
  @Output() sortingData = new EventEmitter<any>();
  @Output() isShowPaginationNext = new EventEmitter<any>();
  @Input() showAction: false;
  @Input() isTeam: boolean;
  @Input() statusLoad: Boolean;
  @Input() expired: Boolean;
  @Input() activeSort: Sort; // Input to receive the active sort state from parent
  // Current paginator page (1-based) and page size are provided by parent when available
  @Input() page: number = 1;
  @Input() pageSize: number = 25;

  @ViewChild('invitedBadge') invitedBadgeRef!: ElementRef;
  @ViewChild(MatSort) matSort: MatSort;

  isAdmin = true;
  isManager = true;
  generalDownloads = [
    { label: 'Windows 7 & 8', link: './../../../assets/Windows/General/Supersee_Installer.bat' },
    { label: 'Linux', link: environment.app.linuxInstallerUrl },
    { label: 'Mac', link: environment.app.macInstallerUrl }
  ];

  silentDownloads = [
    { label: 'Windows 7 & 8' },
    { label: 'Linux' },
    { label: 'Mac' }
  ];


  softwareTypes = [
    {
      value: 'silent',
      label: 'Hidden',
      description: 'Installs silently—no employee login needed.<br> Best for company-managed devices.',
      tooltip: 'The app installs quietly in the background with no employee login. Great for IT-deployed, company-owned devices where you want hands-off setup.'
    },
    {
      value: 'general',
      label: 'Employee Controlled',
      description: 'Employee installs and signs in with their work or personal email.<br> Simple for devices they manage.',
      tooltip: 'The employee installs the app and signs in with their work email and password. Ideal when team members manage their own device (BYOD) and you want an easy setup.'
    },
    {
      value: 'restricted',
      label: 'Admin Controlled',
      description: 'Employee signs in once; tracking always runs.<br> Start/Stop and Sign out are disabled (view-only history).',
      tooltip: 'The employee signs in once and tracking runs automatically. They can\'t Start/Stop or Sign out; they can only view their history. Best for company-owned devices and stricter compliance needs.'
    }
  ];

  // Fixed tooltip texts for menu items to avoid template-time lookups
  selfControlTooltip: string = 'Employees can start or stop tracking themselves and see their own activity details.';
  invisibleTrackingTooltip: string = 'Work activity is tracked automatically in the background, without any action or visible indication to employees.';
  adminControlTooltip: string = 'The company manages tracking. Employees can see their activity, but can’t control tracking.';
  sysConfig = sysConfig

  errorPhotoShow: Boolean = false;
  skeletonLoader: Boolean = true;
  private dbPath: string | null = null;
  userImageClass = 'avatar-img';
  triggerInviteFocusModalOpen: any;
  invitedBadgeObserver: any;
  unobserveInviteBadge: any;
  positionInterval: any;
  globalSettings: any = null;
  currentTooltipUser: any = null;
  currentHealthTooltipUser: any = null;
  healthTooltipPosition: { top: number; left: number } = { top: 0, left: 0 };
  healthTooltipHovered: boolean = false;
  private healthTooltipTimeout: any = null;
  tooltipPosition = { top: 0, left: 0 };
  tooltipHovered: boolean = false;
  shiftTimezone: string | null = null;
  softwareTypeOptions: SoftwareTypeOption[] = [];
  private entitlementsSub?: Subscription;

  constructor(
    private _projectService: ProjectService,
    private db: AngularFireDatabase,
    private dialog: MatDialog,
    private _modalService: ModalService,
    private _snackBar: MatSnackBar,
    public _sharedService: SharedService,
    private router: Router,
    private customtimeAgoPipe: CustomTimeAgoPipe,
    private sweetAlertService: SweetAlertService,
    private route: ActivatedRoute,
    private _dummyDataService: DummyDataService,
    private _cd: ChangeDetectorRef,
    private tokenEncryptionService: TokenEncryptionService,
    private screenshotTime: ScreenshotTimeService,
    private entitlementsService: EntitlementsService,
    private activationJourney: ActivationJourneyService
  ) {
    this.globalSettings = this.tokenEncryptionService.getEncryptedItem<any>('globalSettings');
    this.dbPath = this.tokenEncryptionService.getEncryptedItem<string>('dbPath') as string;
    this.getUSer();
  }

  ngOnInit() {
    const companyId = this.tokenEncryptionService.getEncryptedItem<any>('logedInUser')?.company_id;
    this.screenshotTime.getShiftTimezoneForCompany(companyId).subscribe((tz) => {
      this.shiftTimezone = tz;
    });

    setInterval(() => {
      this.errorPhotoShow = true;
      this.skeletonLoader = false;
    }, 10000);

    this.entitlementsService.syncFromSession();
    this.refreshDownloadSoftwareTypeOptions();
    this.entitlementsSub = this.entitlementsService.stateChanged$.subscribe(() => {
      this.refreshDownloadSoftwareTypeOptions();
    });

    this.setSubjects()
  }

  private refreshDownloadSoftwareTypeOptions(): void {
    this.softwareTypeOptions = getSoftwareTypeOptions(
      this.entitlementsService.getPlanCode(),
      this.entitlementsService.isEnforcementActive(),
      this.entitlementsService.hasCompany(EntitlementKey.hidden_mode)
    );
  }

  isDownloadSoftwareTypeLocked(versionType: SoftwareVersionType): boolean {
    return this.softwareTypeOptions.find((option) => option.value === versionType)?.locked ?? false;
  }

  onLockedDownloadSoftwareTypeClick(versionType: SoftwareVersionType): void {
    this.showSoftwareTypeUpgradeDialog(versionType);
  }

  private showSoftwareTypeUpgradeDialog(versionType: string): void {
    const versionTypeError = validateSoftwareVersionType(
      versionType,
      this.entitlementsService.getPlanCode(),
      this.entitlementsService.isEnforcementActive(),
      this.entitlementsService.hasCompany(EntitlementKey.hidden_mode)
    );
    if (!versionTypeError) {
      return;
    }

    const ref = this.dialog.open(PlanUpgradeDialogComponent, {
      width: '420px',
      maxWidth: '94vw',
      data: {
        title: 'Upgrade required',
        message: versionTypeError,
      },
    });
    ref.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        void this.router.navigate(['/billings'], { queryParams: { intent: 'upgrade' } });
      }
    });
  }

  private blockDownloadIfSoftwareTypeLocked(versionType: SoftwareVersionType): boolean {
    const versionTypeError = validateSoftwareVersionType(
      versionType,
      this.entitlementsService.getPlanCode(),
      this.entitlementsService.isEnforcementActive(),
      this.entitlementsService.hasCompany(EntitlementKey.hidden_mode)
    );
    if (versionTypeError) {
      this.showSoftwareTypeUpgradeDialog(versionType);
      return true;
    }
    return false;
  }

  ngAfterViewInit() {
    // Restore sort state if it exists
    if (this.matSort && this.activeSort) {
      this.restoreSortState();
    }
  }

  ngOnChanges(changes: SimpleChanges) {

    // When activeSort changes or developerList changes, restore the sort state
    if (changes['activeSort'] || changes['developerList']) {
      if (this.matSort) {
        // Use setTimeout to ensure the view is updated
        setTimeout(() => {
          this.restoreSortState();
        }, 0);
      }
    }
  }

  private restoreSortState() {
    if (!this.matSort) return;

    // If there's an active sort, restore it
    if (this.activeSort && this.activeSort.active && this.activeSort.direction) {
      // Only update if the sort state is different to avoid unnecessary updates
      if (this.matSort.active !== this.activeSort.active || this.matSort.direction !== this.activeSort.direction) {
        // Programmatically set the sort state on MatSort without emitting event
        // This just updates the UI indicator, the data is already sorted by the parent
        this.matSort.active = this.activeSort.active;
        this.matSort.direction = this.activeSort.direction;
        // Trigger change detection to update the sort header indicators
        this._cd.detectChanges();
      }
    } else if (this.matSort.active) {
      // If sort was cleared, reset the MatSort state
      this.matSort.active = '';
      this.matSort.direction = '';
      this._cd.detectChanges();
    }
  }

  ngOnDestroy(): void {
    this.entitlementsSub?.unsubscribe();

    if (this.triggerInviteFocusModalOpen) {
      this.triggerInviteFocusModalOpen.unsubscribe();
    }

    if (this.invitedBadgeObserver) {
      if (this.invitedBadgeObserver && this.invitedBadgeRef?.nativeElement) {
        this.invitedBadgeObserver.unobserve(this.invitedBadgeRef.nativeElement);
      }
      this.invitedBadgeObserver.disconnect();
    }

    clearInterval(this.positionInterval);
    this.stopDemoToasts();
  }

  // Optimized helper methods for template
  trackByUserId(index: number, user: any): string {
    return user.id;
  }


  isLogoutHealthEntry(entry: any): boolean {
    if (!entry) return false;
    const state = (entry?.state || '').toUpperCase();
    if (state === 'LOGOUT' || state === 'LOGGED_OUT') return true;

    const rawCodes = this.getReasonCodes(entry?.reasonCode || entry?.reason);
    if (rawCodes.length === 0) return false;

    return rawCodes.every(c => {
      const upper = c.toUpperCase();
      return upper === 'ERR_USER_LOGGED_OUT' || upper === 'USER LOGGED OUT';
    });
  }

  getHealthItemState(item: any): string {
    if (!item) return 'UNKNOWN';
    if (this.isLogoutHealthEntry(item)) return 'LOGOUT';
    return (item.state || 'UNKNOWN').toUpperCase();
  }

  getUserHealthState(user: any): 'HEALTHY' | 'UNHEALTHY' | 'STARTING' | 'UNINSTALLED' | 'LOGOUT' | 'UNKNOWN' {
    const history = user?.agentIntegrity?.healthHistory;
    if (Array.isArray(history) && history.length > 0) {
      const latest = history[history.length - 1];
      if (this.isLogoutHealthEntry(latest)) return 'LOGOUT';
      const state = (latest?.state || '').toUpperCase();
      if (state === 'HEALTHY') return 'HEALTHY';
      if (state === 'UNHEALTHY') return 'UNHEALTHY';
      if (state === 'STARTING') return 'STARTING';
      if (state === 'UNINSTALLED') return 'UNINSTALLED';
      if (state === 'LOGOUT' || state === 'LOGGED_OUT') return 'LOGOUT';
    }
    if (user?.uninstall?.isUninstalled) return 'UNINSTALLED';
    return 'UNKNOWN';
  }

  getLatestHealthEntry(user: any): any {
    const history = user?.agentIntegrity?.healthHistory;
    if (Array.isArray(history) && history.length > 0) {
      return history[history.length - 1];
    }
    return null;
  }

  getHealthHistory(user: any): any[] {
    const history = user?.agentIntegrity?.healthHistory;
    if (Array.isArray(history)) {
      return [...history].reverse();
    }
    return [];
  }

  getReasonCodes(reasonCode?: string): string[] {
    return this._sharedService.getReasonCodes(reasonCode);
  }

  getSingleComponent(component: string): string {
    return this._sharedService.getSingleComponent(component);
  }

  getHumanReadableComponent(component?: string): string {
    return this._sharedService.getHumanReadableComponent(component);
  }

  getSingleReason(code: string): string {
    return this._sharedService.getSingleReason(code);
  }

  getHumanReadableReasonList(reasonCode?: string): string[] {
    return this._sharedService.getHumanReadableReasonList(reasonCode);
  }

  getReasonCodeByIndex(reasonCode?: string, index: number = 0): string {
    return this._sharedService.getReasonCodeByIndex(reasonCode, index);
  }

  getReasonFix(code: string): string {
    return this._sharedService.getReasonFix(code);
  }

  getReasonFixSteps(code: string): string[] {
    return this._sharedService.getReasonFixSteps(code);
  }

  getHumanReadableReason(reasonCode?: string): string {
    return this._sharedService.getHumanReadableReason(reasonCode);
  }

  formatHealthTimestamp(utcTimestamp?: string, format: string = 'MMM D, YYYY h:mm:ss A'): string {
    return this._sharedService.formatHealthTimestamp(utcTimestamp, format);
  }

  getHealthRelativeTime(utcTimestamp?: string): string {
    return this._sharedService.getHealthRelativeTime(utcTimestamp);
  }

  getHealthStatusIcon(user: any): string {
    const state = this.getUserHealthState(user);
    if (state === 'UNHEALTHY') return 'error';
    if (state === 'UNINSTALLED') return 'cancel';
    if (state === 'LOGOUT') return 'logout';
    if (state === 'HEALTHY') return 'check_circle';
    if (state === 'STARTING') return 'sync';
    return 'info';
  }

  getHealthStatusLabel(user: any): string {
    const state = this.getUserHealthState(user);
    if (state === 'UNHEALTHY') return 'Agent Unhealthy';
    if (state === 'UNINSTALLED') return 'Agent Uninstalled';
    if (state === 'LOGOUT') return 'User Logout';
    if (state === 'HEALTHY') return 'Agent Healthy';
    if (state === 'STARTING') return 'Agent Starting';
    return 'Agent Unknown';
  }

  getHealthStatusColorClass(user: any): string {
    const state = this.getUserHealthState(user);
    if (state === 'HEALTHY') return 'text-emerald-600';
    if (state === 'STARTING') return 'text-blue-600';
    if (state === 'UNHEALTHY' || state === 'UNINSTALLED' || state === 'LOGOUT') return 'text-red-600';
    return 'text-gray-600';
  }

  showHealthTooltip(event: MouseEvent, user: any): void {
    if (this.healthTooltipTimeout) {
      clearTimeout(this.healthTooltipTimeout);
      this.healthTooltipTimeout = null;
    }
    this.currentHealthTooltipUser = user;
    user.showHealthTooltip = true;

    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    const tooltipWidth = 360;

    const historyCount = user?.agentIntegrity?.healthHistory?.length || 1;
    const isUnhealthy = this.getUserHealthState(user) === 'UNHEALTHY';
    const estimatedHeight = Math.min(320, 95 + (isUnhealthy ? 70 : 0) + Math.min(historyCount, 3) * 45);

    const isInMenu = !!target.closest('.mat-mdc-menu-panel, .mat-menu-panel, .employee-actions-menu, .more-button-dropdowns');
    let left: number;
    let top: number;

    if (isInMenu && rect.left - tooltipWidth - 12 >= 10) {
      // Place to the left of the dropdown menu
      left = rect.left - tooltipWidth - 12;
      top = rect.top - 20;
      if (top + estimatedHeight > window.innerHeight - 15) {
        top = Math.max(10, window.innerHeight - estimatedHeight - 15);
      }
      if (top < 10) {
        top = 10;
      }
    } else {
      left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
      if (left + tooltipWidth > window.innerWidth - 20) {
        left = window.innerWidth - tooltipWidth - 20;
      }
      if (left < 20) {
        left = 20;
      }

      top = rect.bottom + 8;
      if (top + estimatedHeight > window.innerHeight - 15) {
        top = Math.max(10, rect.top - estimatedHeight - 8);
      }
    }

    this.healthTooltipPosition = { top, left };

    setTimeout(() => {
      if (this.currentHealthTooltipUser !== user) return;
      const card = document.querySelector('.health-tooltip-card') as HTMLElement;
      if (card && card.offsetHeight > 0) {
        const actualH = card.offsetHeight;
        if (isInMenu && rect.left - tooltipWidth - 12 >= 10) {
          let newTop = rect.top - 20;
          if (newTop + actualH > window.innerHeight - 15) {
            newTop = Math.max(10, window.innerHeight - actualH - 15);
          }
          if (newTop < 10) {
            newTop = 10;
          }
          this.healthTooltipPosition = { top: newTop, left };
        } else {
          let newTop = rect.bottom + 8;
          if (newTop + actualH > window.innerHeight - 15) {
            newTop = Math.max(10, rect.top - actualH - 8);
          }
          this.healthTooltipPosition = { top: newTop, left };
        }
      }
    }, 0);
  }

  hideHealthTooltip(user?: any): void {
    this.healthTooltipTimeout = setTimeout(() => {
      if (!this.healthTooltipHovered) {
        this.clearHealthTooltip();
      }
    }, 200);
  }

  clearHealthTooltip(): void {
    if (this.healthTooltipTimeout) {
      clearTimeout(this.healthTooltipTimeout);
      this.healthTooltipTimeout = null;
    }
    if (this.currentHealthTooltipUser) {
      this.currentHealthTooltipUser.showHealthTooltip = false;
    }
    this.currentHealthTooltipUser = null;
    this.healthTooltipHovered = false;
  }

  updateTooltipPosition(event: any, user: any): void {
    this.currentTooltipUser = user;
    const element = event.target as HTMLElement;
    const rect = element.getBoundingClientRect();

    // Position tooltip below the element with some offset
    this.tooltipPosition = {
      top: rect.bottom + 10, // 10px below the element
      left: rect.left
    };
  }

  clearTooltip(): void {
    this.currentTooltipUser = null;
    this.tooltipHovered = false;
  }

  getRowNumber(indexInPage: number): number {
    const page = this.page && this.page > 0 ? this.page : 1;
    const size = this.pageSize && this.pageSize > 0 ? this.pageSize : 25;
    return (page - 1) * size + indexInPage + 1;
  }

  getUserRole(role: string[]): string {
    return role.length > 1 ? role[1] : role[0];
  }

  getLastScreenshot(lastScreenshot: any): string {
    if (!lastScreenshot || this.expired) {
      return '--';
    }
    const instant = this.screenshotTime.parseCaptureToUtc(lastScreenshot, this.shiftTimezone);
    if (instant && instant.isValid) {
      return this.customtimeAgoPipe.transform(instant.toJSDate(), true);
    }
    return this.customtimeAgoPipe.transform(lastScreenshot, true);
  }

  getScreenshotAgeMs(user: any): number | null {
    const lastScreenshot = user?.meta?.last_screenshot;
    if (!lastScreenshot || this.expired) {
      return null;
    }
    const instant = this.screenshotTime.parseCaptureToUtc(lastScreenshot, this.shiftTimezone);
    let ms: number;
    if (instant && instant.isValid) {
      ms = instant.toMillis();
    } else {
      const parsed = new Date(lastScreenshot).getTime();
      ms = Number.isFinite(parsed) ? parsed : NaN;
    }
    if (!Number.isFinite(ms)) {
      return null;
    }
    return Date.now() - ms;
  }

  isStaleScreenshot(user: any): boolean {
    if (!this.isLiveActivityStatus(user)) {
      return false;
    }
    const age = this.getScreenshotAgeMs(user);
    if (age == null) {
      return true;
    }
    return age > STALE_LIVE_SCREENSHOT_MS;
  }

  /** Mute Last screenshot / Last app when the row is not live, without hiding the values. */
  isActivityQuiet(user: any): boolean {
    return isEmployeesListActivityQuiet({
      status: user?.status,
      screenshotAgeMs: this.getScreenshotAgeMs(user),
      expired: !!this.expired,
    });
  }

  getScreenshotDisplay(user: any): string {
    if (!user?.meta?.last_screenshot || this.expired) {
      return formatScreenshotCell('--');
    }
    return formatScreenshotCell(this.getLastScreenshot(user.meta.last_screenshot));
  }

  getScreenshotTooltip(user: any): string {
    if (!user?.meta?.last_screenshot) {
      return 'No screenshot yet';
    }
    const instant = this.screenshotTime.parseCaptureToUtc(user.meta.last_screenshot, this.shiftTimezone);
    const at = instant?.isValid ? instant.toJSDate() : new Date(user.meta.last_screenshot);
    if (!at || Number.isNaN(at.getTime())) {
      return this.getLastScreenshot(user.meta.last_screenshot);
    }
    const exact = at.toLocaleString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
    if (this.isLiveActivityStatus(user) && this.isStaleScreenshot(user)) {
      return `${exact} — not recent enough to treat as current`;
    }
    return exact;
  }

  getLastApplication(lastAppName: string): string {
    return (lastAppName && !this.expired)
      ? lastAppName.slice(0, 20)
      : '--';
  }

  // Status-related helper methods
  getUserStatusType(user: any): string {
    if (this.expired) return 'none';
    return getEmployeeListStatusType(user, (status) =>
      this._sharedService.getDayDifferenceForAbsent(status)
    );
  }

  getAutoLogoutTooltip(user: any): string {
    return `${user.logoutReason?.reason} Time: ${user.logoutReason?.time}`;
  }

  getOfflineTooltip(user: any): string {
    return this.getPresenceTooltip(user) || 'Last seen —';
  }

  getPresenceCaption(user: any): string {
    return getPresenceCaption({
      statusType: this.getUserStatusType(user),
      lastSeenMs: getLastSeenMs(user),
      idleStartMs: parsePresenceTimeMs(user?.idleStartTime),
    });
  }

  getPresenceTooltip(user: any): string {
    return getPresenceTooltip({
      statusType: this.getUserStatusType(user),
      lastSeenMs: getLastSeenMs(user),
      idleStartMs: parsePresenceTimeMs(user?.idleStartTime),
    });
  }

  isLiveActivityStatus(user: any): boolean {
    return isLiveActivityStatus(user?.status);
  }

  getSignedOutLabel(user: any): string {
    return getSignedOutLabelForUser(user, this.getUserStatusType(user));
  }

  getPasswordResetLabel(user: any): string {
    return getPasswordResetLabelForUser(user, this.getUserStatusType(user));
  }

  getIdleTooltip(user: any): string {
    return this.getPresenceTooltip(user) || 'Idle';
  }

  // Action-related helper methods
  getActionClass(user: any): string {
    return user.status === 'online' ? 'online' : 'offline';
  }

  handleScreenshotRequest(user: any): void {
    if (this.isPresenceActionEnabled(user)) {
      this.screenshotRequest(user.id, user.status, user.name);
    }
  }

  handleScreencastRequest(user: any): void {
    if (this.isPresenceActionEnabled(user)) {
      this.openScreencastActionModel(user);
    }
  }

  trackTableActionMenu(action: string): void {
    trackClarityEvent('table_action_menu_select', { action });
  }

  /** Watch live / Take screenshot are only available while the agent is online or idle. */
  isPresenceActionEnabled(user: any): boolean {
    return !this.expired && (user?.status === 'online' || user?.status === 'idle');
  }

  presenceActionDisabledTooltip(user: any): string {
    return this.isPresenceActionEnabled(user) ? '' : 'Employee agent is offline';
  }

  canAccessWorkReplay(): boolean {
    return this.entitlementsService.canAccessWorkReplay();
  }

  showWorkReplayAction(): boolean {
    if (!this.entitlementsService.isEnforcementActive()) {
      return this.entitlementsService.hasCompany(EntitlementKey.work_replay)
        || this.entitlementsService.hasCompany(EntitlementKey.work_replay_preview);
    }
    return true;
  }

  isWorkReplayLocked(): boolean {
    return this.entitlementsService.isEnforcementActive() && !this.canAccessWorkReplay();
  }

  canAccessProductivityTrends(): boolean {
    return this.entitlementsService.canAccessProductivityTrends();
  }

  showProductivityTrendsAction(): boolean {
    if (!this.entitlementsService.isEnforcementActive()) {
      return this.entitlementsService.hasCompany(EntitlementKey.productivity_trends)
        || this.entitlementsService.hasCompany(EntitlementKey.productivity_trends_preview);
    }
    return true;
  }

  isProductivityTrendsLocked(): boolean {
    return this.entitlementsService.isEnforcementActive() && !this.canAccessProductivityTrends();
  }

  canAccessDeviceManagement(): boolean {
    return this.entitlementsService.hasCompany(EntitlementKey.device_management);
  }

  showDeviceManagementAction(): boolean {
    if (!this.isAdmin) {
      return false;
    }
    if (!this.entitlementsService.isEnforcementActive()) {
      return this.canAccessDeviceManagement();
    }
    return true;
  }

  isDeviceManagementLocked(): boolean {
    return this.entitlementsService.isEnforcementActive() && !this.canAccessDeviceManagement();
  }

  openWorkReplay(user: any): void {
    this.trackTableActionMenu('work_replay');
    if (this.isWorkReplayLocked()) {
      void this.router.navigate(['/feature-locked'], {
        queryParams: {
          keys: EntitlementKey.work_replay,
          returnUrl: '/employees',
        },
      });
      return;
    }
    void this.router.navigate(['/work-replay'], {
      queryParams: { employeeId: user?.id },
    });
  }

  openScreenshots(user: any): void {
    this.trackTableActionMenu('screenshot_gallery');
    void this.router.navigate(['/screenshots'], {
      queryParams: { employeeId: user?.id },
    });
  }

  openProductivityTrends(user: any): void {
    this.trackTableActionMenu('productivity_trends');
    if (this.isProductivityTrendsLocked()) {
      void this.router.navigate(['/feature-locked'], {
        queryParams: {
          keys: EntitlementKey.productivity_trends,
          returnUrl: '/employees',
        },
      });
      return;
    }
    void this.router.navigate(['/productivity-trends'], {
      queryParams: { employeeId: user?.id },
    });
  }

  openDeviceManagement(user: any): void {
    this.trackTableActionMenu('device_management');
    if (this.isDeviceManagementLocked()) {
      void this.router.navigate(['/feature-locked'], {
        queryParams: {
          keys: EntitlementKey.device_management,
          returnUrl: '/employees',
        },
      });
      return;
    }
    void this.router.navigate(['/device-management'], {
      queryParams: { employee_id: user?.id },
    });
  }

  // Agent info helper methods
  shouldShowMode(user: any): boolean {
    if (user?.appInfo?.label && (user.appInfo.label === 'S' || user.appInfo.label === 'G' || user.appInfo.label === 'R')) {
      return true;
    }
    if (user?.appInfo?.version) {
      return true;
    }
    return false;
  }

  getModeIcon(user: any): string {
    const mode = getEmployeeTrackingMode(user, this.globalSettings || {});
    if (mode === 'hidden') {
      return 'visibility_off';
    }
    if (mode === 'admin_controlled') {
      return 'admin_panel_settings';
    }
    if (mode === 'employee_controlled') {
      return 'person_outline';
    }
    return '';
  }

  getModeTooltip(user: any): string {
    const mode = getEmployeeTrackingMode(user, this.globalSettings || {});
    if (mode === 'hidden') {
      return 'Hidden';
    }
    if (mode === 'admin_controlled') {
      return 'Admin Controlled';
    }
    if (mode === 'employee_controlled') {
      return 'Employee Controlled';
    }
    return 'N/A';
  }

  getOSIconUrl(os: string): string {
    const baseUrl = 'https://us.aptabase.com/assets/';

    switch (os) {
      case 'win32':
        return `${baseUrl}windows-cfcb5d46.svg`;
      case 'linux':
        return `${baseUrl}ubuntu-9a344cb4.svg`;
      case 'darwin':
        return `${baseUrl}macos-e20f2458.svg`;
      default:
        return '';
    }
  }


  // -------------------- DEMO TOASTS --------------------
  private readonly enableDemoRandomToasts: boolean = true;
  private demoToastTimer: any;
  private readonly demoAppNames: string[] = [
    'Google Chrome', 'Slack', 'Figma', 'Discord', 'Outlook', 'Visual Studio Code', 'Microsoft Teams', 'Windows Explorer'
  ];
  private demoUserQueue: any[] = [];

  private withDemoMeta(user: any) {
    const pickApp = () => this.demoAppNames[Math.floor(Math.random() * this.demoAppNames.length)];
    const minutesAgo = Math.floor(Math.random() * 120) + 1; // 1..120 minutes ago
    const lastScreenshotISO = new Date(Date.now() - minutesAgo * 60 * 1000).toISOString();
    return {
      ...user,
      meta: {
        ...(user.meta || {}),
        last_screenshot: user.meta?.last_screenshot || lastScreenshotISO,
        last_application_name: user.meta?.last_application_name || pickApp(),
        settings: user.meta?.settings || {}
      }
    };
  }

  private isDummyMode: boolean = false;

  private updateDemoUserActivity(userId: any, appName: string) {
    // const idx = this.usersLists.findIndex(u => (u.id || u.name) === userId);
    const idx = this.filteredUserList.findIndex(u => (u.id || u.name) === userId);
    if (idx !== -1) {
      const updated = { ...this.usersLists[idx] };
      updated.meta = { ...(updated.meta || {}) };
      updated.meta.last_application_name = appName;
      updated.meta.last_screenshot = new Date().toISOString();
      // Replace the element to ensure change detection if needed
      this.usersLists = [
        ...this.usersLists.slice(0, idx),
        updated,
        ...this.usersLists.slice(idx + 1)
      ];

      this.filteredUserList = this.usersLists;
    }
  }

  private rebuildDemoUserQueue() {
    if (!this.filteredUserList || !this.filteredUserList.length) {
      this.demoUserQueue = [];
      return;
    }
    const ids = this.filteredUserList.map(u => u.id || u.name);
    this.demoUserQueue = this.shuffleArray(ids);
  }

  private getNextDemoUserId(): any {
    if (!this.demoUserQueue.length) {
      this.rebuildDemoUserQueue();
    }
    return this.demoUserQueue.shift();
  }

  private shuffleArray<T>(array: T[]): T[] {
    const clone = array.slice();
    for (let i = clone.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [clone[i], clone[j]] = [clone[j], clone[i]];
    }
    return clone;
  }

  private startDemoToasts() {
    if (this.demoToastTimer) return;
    if (!this.demoUserQueue.length) this.rebuildDemoUserQueue();
    this.demoToastTimer = setInterval(() => {
      if (!this.usersLists || !this.usersLists.length) return;
      const nextId = this.getNextDemoUserId();
      const randomUser = this.usersLists.find(u => (u.id || u.name) === nextId) || this.usersLists[0];
      const types = ['Periodic', 'App Changed'];
      const type = types[Math.floor(Math.random() * types.length)];
      const appName = this.demoAppNames[Math.floor(Math.random() * this.demoAppNames.length)];
      const demoToast = {
        type: type,
        userName: randomUser?.name || 'User',
        userId: randomUser?.id || randomUser?.name,
        appName: appName,
        imgURL: '/assets/images/screenshot.png',
        isDemo: true
      };
      this._sharedService.showToast(demoToast);
      this.updateDemoUserActivity(randomUser?.id || randomUser?.name, appName);
    }, 15000);
  }

  private stopDemoToasts() {
    if (this.demoToastTimer) {
      clearInterval(this.demoToastTimer);
      this.demoToastTimer = null;
    }
  }

  openModalFire(id: any) {
    this.usersLists.find((dev) => {
      if (dev.id == id) {
        dev.isMenuOpened = false;
      }
    });
    if (this._modalService.isAlreadyOpenDialog(CreateUserComponent)) {
      this._modalService.closeDialogIfOpen(CreateUserComponent);
    }

    // Persist edit-employee in URL
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { 'edit-employee': id },
      queryParamsHandling: 'merge'
    });

    const dialogRef = this._modalService.openCommonMatDialog(
      CreateUserComponent,
      {
        className: 'create-user-modal',
        maxHeight: '80vh',
        disableClose: false,
        componentData: { isUserEditable: true, editUserId: id },
        // position: { top: '30px' }
      }
    );

    // Remove param when dialog closes
    if (dialogRef && dialogRef.afterClosed) {
      dialogRef.afterClosed().subscribe(() => {
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { 'edit-employee': null },
          queryParamsHandling: 'merge'
        });
      });
    }
  }

  deleteUser(id, dev) {
    let data = {
      id: id,
      name: dev.name,
      type: "employee"
    };
    this.usersLists.find((dev) => {
      if (dev.id == id) {
        dev.isMenuOpened = false;
      }
    });
    this.openDialog(ConfirmationDialogComponent, data).subscribe((response) => {
      if (response != undefined) {
        this.sweetAlertService.showCustomAlert({
          title: 'Deleting Employee',
          text: `${dev.name} will be deleted shortly.`,
          icon: 'info',
          timer: 3000,
          showConfirmButton: false
        });

        this._projectService
          .DeleteEmployee(response.delete)
          .subscribe((data: any) => {
            this.deleteEmployeee.emit({ data: data });

            // If the deleted employee was the onboarding test employee, clear the local storage
            // so the Test Data Cleanup banner disappears automatically.
            try {
              const testEmpStr = localStorage.getItem('supersee_v2_test_employee');
              if (testEmpStr) {
                const testEmpData = JSON.parse(testEmpStr);
                if (testEmpData?.employee?.id === response.delete) {
                  localStorage.removeItem('supersee_v2_test_employee');
                }
              }
            } catch (e) {
              console.error('Failed to clear test employee from local storage', e);
            }

            // Show success message and redirect to employees list after successful deletion
            this.sweetAlertService.showCustomAlert({
              title: 'Success!',
              text: 'Employee deleted successfully',
              icon: 'success',
              timer: 2000,
              showConfirmButton: false
            });

            // Redirect after a short delay to allow the success message to be visible
            setTimeout(() => {
              this.router.navigate(['/employees']);
            }, 2500);
          });
      }
    });
  }

  openDialog(someComponent, data = {}): Observable<any> {
    const dialogRef = this.dialog.open(someComponent, { data, panelClass: 'centered-dialog' });
    return dialogRef.afterClosed();
  }

  openScreencastActionModel(user) {
    let obj = {
      userDetails: user,
      userId: user.id,
      dbPath: this.dbPath,
    };
    let data = obj;
    const dialogRef = this.openDialog(
      ScreencastActionModelComponent,
      data
    ).subscribe((response) => { });
  }

  screenshotRequest(id, Status, name) {
    if (!navigator.onLine) {
      this._snackBar.open("Please check your internet connection?", "Undo", {
        duration: 3000,
      });
    } else {
      if (!this._sharedService.canTriggerScreenshotRequest(id)) {
        return;
      }
      let data = {
        type: "Requesting Screenshot",
        userName: name,
      };
      this._sharedService.showToast(data);

      // If no Firebase path is configured (demo/dummy mode), simulate a delivered screenshot toast after a short delay
      if (this.isDummyMode) {
        setTimeout(() => {
          const demoToast = {
            type: 'Request',
            userName: name,
            userId: id,
            appName: 'Google Chrome',
            imgURL: '/assets/images/screenshot.png',
            isDemo: true
          };
          this._sharedService.showToast(demoToast);
          this.updateDemoUserActivity(id, 'Google Chrome');
        }, 3500);
        return;
      }

      setTimeout(() => {
        this.db.database.ref(`${this.dbPath}`).child(`${id}`).get().then((res) => {
          // Soft failure toast only — do not write ssRequest:false (agent ack race).
          if (res.val()?.ssRequest == null) {
            let data = {
              type: "Cant get screenshot",
              userName: name,
            };
            this._sharedService.showToast(data);
          }
        });
      }, 20000)
      // Match presence actions / tabs: idle agents are reachable; online-only skipped the Firebase trigger.
      if (Status == "online" || Status == "idle") {
        this._sharedService.startOnDemandScreenshotRequest({
          employeeId: id,
          dbPath: this.dbPath,
          source: 'employees_list',
        });
      }
    }
  }

  sortData(sort: Sort) {
    this.sortingData.emit(sort);
  }

  openMenu(dev) {
    this.usersLists.find((singleDev) => {
      if (singleDev.id != dev.id) {
        singleDev.isMenuOpened = false;
      }
    });
    if (dev.isMenuOpened) {
      dev.isMenuOpened = false;
    } else {
      dev.isMenuOpened = true;
    }
    return dev;
  }

  /** True when this employee has personalized tracking settings turned on. */
  hasPersonalizedSettings(user: any): boolean {
    const value = user?.meta?.settings?.ss_personalizeSettings;
    return value === true || String(value ?? '').toLowerCase() === 'true';
  }

  isPersonalizeSetting(setting) {
    if (!setting) {
      return true;
    }
    if (Array.isArray(setting)) {
      if (!setting.length) {
        return true;
      }
    } else {
      if (setting.ss_personalizeSettings) {
        if (setting.ss_personalizeSettings == 'true') {
          return false;
        } else {
          return true;
        }
      } else {
        return true;
      }
    }
  }

  getUSer() {
    if (this.isTourActive()) {
      let roleArr = [];
      this._projectService.getUser().subscribe((data: any) => {
        roleArr = data.role;

        let adminRole = roleArr.filter((ele) => {
          return (ele.toLowerCase().includes("admin") || ele.toLowerCase().includes("company"));
        })
        if (adminRole.length == 0) {
          this.isAdmin = false;
        } else {
          this.isAdmin = true;
        }

        let managerRole = roleArr.filter((ele) => {
          return (ele.toLowerCase().includes("manager"));
        })
        if (managerRole.length == 0) {
          this.isManager = false;
        } else {
          this.isManager = true;
        }
      })
    } else {
      let roleArr = [];
      this._projectService.getUser().subscribe((data: any) => {
        roleArr = data.role;
        let adminRole = roleArr.filter((ele) => {
          return (ele.toLowerCase().includes("admin") || ele.toLowerCase().includes("company"));
        })
        if (adminRole.length == 0) {
          this.isAdmin = false;
        } else {
          this.isAdmin = true;
        }

        let managerRole = roleArr.filter((ele) => {
          return (ele.toLowerCase().includes("manager"));
        })
        if (managerRole.length == 0) {
          this.isManager = false;
        } else {
          this.isManager = true;
        }
      })
    }
  }

  isTourActive() {
    if (localStorage.getItem('isTour')) {
      if (localStorage.getItem('isTour') == 'false') {
        return true;
      } else {
        return false;
      }
    } else {
      return true;
    }
  }

  checkIsActive() {
    if (this.router.url.includes('/employees') == true) {
      return true;
    } else {
      return false;
    }
  }

  resetPassword(user: any) {
    const dialogData = {
      id: user.id,
      email: user.email
    }
    this.dialog.open(NewPasswordComponent, {
      width: '660px',
      data: dialogData,
      panelClass: 'centered-dialog',
    })
  }

  openPasswordResets(user: any) {
    this.dialog.open(PasswordResetsDialogComponent, {
      width: '420px',
      data: {
        userId: user?.id,
        employeeName: user?.name || '',
      },
      panelClass: 'centered-dialog',
    });
  }

  autoResetPassword(label, user) {
    if (this.blockDownloadIfSoftwareTypeLocked('silent')) {
      return;
    }

    const hiddenCopy = installerConfirmCopy.hiddenHtml(
      resolveEmployeeFirstName(user),
      isInstallerFirstTimeEmployee(user)
    );
    swal.fire({
      title: hiddenCopy.title,
      html: hiddenCopy.html,
      showCancelButton: true,
      confirmButtonText: hiddenCopy.confirmText,
      confirmButtonColor: colors.purple,
      cancelButtonColor: '#6c757d',
      cancelButtonText: hiddenCopy.cancelText,
    }).then((result) => {
      if (result.isConfirmed) {
        let password = this.generatePassword()
        const resetPayload = { id: user.id, email: user.email, password: password };

        this
          ._projectService
          .resetUserPassword(resetPayload).subscribe((userData: any) => {
            this._projectService.downloadAgentInstaller(user, label, 'silent', password);
          }, err => {
            console.error('Error resetting password:', err);
            swal.fire({
              icon: 'error',
              title: "Couldn't reset the password",
              text: 'The installer was not downloaded. Try again. If this keeps happening, contact support.',
              confirmButtonText: 'Close',
            });
          })
      }
    })
  }

  downloadFile(label, dev, password) {
    this._projectService.downloadAgentInstaller(dev, label, 'silent', password);
  }

  generatePassword() {
    const minLength = 8;
    const maxLength = 12;
    const specialChars = ['@'];
    const uppercaseChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercaseChars = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const allChars = uppercaseChars + lowercaseChars + numbers;

    const length = Math.floor(Math.random() * (maxLength - minLength + 1)) + minLength;

    let password = '';
    password += uppercaseChars[Math.floor(Math.random() * uppercaseChars.length)]; // One uppercase
    password += lowercaseChars[Math.floor(Math.random() * lowercaseChars.length)]; // One lowercase
    password += numbers[Math.floor(Math.random() * numbers.length)]; // One number

    let specialChar = specialChars[Math.floor(Math.random() * specialChars.length)]; // One special char

    let remainingLength = length - 4; // Already used 3 characters, plus the special character

    for (let i = 0; i < remainingLength; i++) {
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }

    let middleIndex = Math.floor(password.length / 2);
    password = password.slice(0, middleIndex) + specialChar + password.slice(middleIndex);

    return password;
  }

  downloadGeneralAgent(osLabel: string) {
    this._projectService.downloadAgentInstaller(null, osLabel, 'general');
  }

  downloadRustGeneralAgent(user?: any): void {
    if (user?.id) {
      this._projectService.applyEmployeeControlAndDownloadRust('Windows', user);
      return;
    }
    this._projectService.downloadRustGeneralAgent();
  }

  autoResetPasswordRust(label: string, user: any): void {
    if (this.blockDownloadIfSoftwareTypeLocked('silent')) {
      return;
    }
    this._projectService.autoResetPasswordRust(label, user);
  }

  downloadRustAdminControlAgent(osLabel: string, user: any): void {
    if (this.blockDownloadIfSoftwareTypeLocked('restricted')) {
      return;
    }

    const isCompanySettings = this.isPersonalizeSetting(user?.meta?.settings);
    const dialogRef = this.dialog.open(AdminControlConfirmDialogComponent, {
      width: '460px',
      disableClose: true,
      panelClass: 'centered-dialog',
      data: installerConfirmCopy.adminControlled(
        resolveEmployeeFirstName(user),
        isCompanySettings,
        isInstallerFirstTimeEmployee(user)
      ),
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.confirmed) {
        this._projectService.applyAdminControlAndDownloadRust(osLabel, user);
      }
    });
  }

  downloadAdminControlAgent(osLabel: string, user: any) {
    if (this.blockDownloadIfSoftwareTypeLocked('restricted')) {
      return;
    }

    const isCompanySettings = this.isPersonalizeSetting(user?.meta?.settings);
    const dialogRef = this.dialog.open(AdminControlConfirmDialogComponent, {
      width: '460px',
      disableClose: true,
      panelClass: 'centered-dialog',
      data: installerConfirmCopy.adminControlled(
        resolveEmployeeFirstName(user),
        isCompanySettings,
        isInstallerFirstTimeEmployee(user)
      ),
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (!result) { return; }
      if (result.confirmed) { this.applyAdminControlAndDownload(osLabel, user); }
    });
  }

  private applyAdminControlAndDownload(osLabel: string, user: any) {
    // Generate password for the installer (same flow as silent: reset then download with credentials)
    const password = this.generatePassword();
    const resetPayload = { id: user.id, email: user.email, password };

    this._projectService.resetUserPassword(resetPayload).subscribe(
      () => {
        this._projectService.createAdminControlUserSettings(user.id).subscribe(
          () => {
            this._projectService.getUserList(0, 0, true).subscribe(() => {
              this._projectService.updateEmployeeList({ refresh: true });
            });
            this._projectService.downloadAgentInstaller(user, osLabel, 'restricted', password);
          },
          (err) => {
            console.error('Failed to apply Admin Control settings before download', err);
            this._snackBar.open('Could not apply Admin Control settings. Downloading installer anyway.', 'Close', { duration: 3000 });
            this._projectService.downloadAgentInstaller(user, osLabel, 'restricted', password);
          }
        );
      },
      (err) => {
        console.error('Error resetting password for Admin Control download', err);
        this._snackBar.open('Could not set password. Download aborted.', 'Close', { duration: 3000 });
      }
    );
  }

  showFocusPopup() {
    // Check if other modals are open before showing invite modal
    if (this.dialog.openDialogs.length > 0) {
      return;
    }

    // Check if we're in suppression mode
    const suppressInviteModal = this.route.snapshot.queryParams['suppressInviteModal'];
    if (suppressInviteModal === 'true' || this.activationJourney.shouldSuppressInviteFocusCoach()) {
      return;
    }

    setTimeout(() => {
      // Double-check if modals are still closed before proceeding
      if (this.dialog.openDialogs.length > 0) {
        return;
      }

      const rect = this.invitedBadgeRef.nativeElement.getBoundingClientRect();
      this._sharedService.openInvitedFocusDialog.next(
        {
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height
        }
      )
      this.isOpenFirstTimeModal = true;

      this.invitedBadgeObserver = new ResizeObserver(() => {
        let updatedData = this.invitedBadgeRef.nativeElement.getBoundingClientRect();
        this._sharedService.openInvitedFocusDialog.next(
          {
            top: updatedData.top,
            left: updatedData.left,
            width: updatedData.width,
            height: updatedData.height
          }
        )
      })

      this.invitedBadgeObserver.observe(this.invitedBadgeRef.nativeElement);
      this.positionInterval = setInterval(() => {
        const position = this.invitedBadgeRef.nativeElement.getBoundingClientRect();
        this._sharedService.openInvitedFocusDialog.next(
          {
            top: position.top,
            left: position.left,
            width: position.width,
            height: position.height
          }
        )
      }, 1000); // Adjust interval as needed
    }, 500);
  }

  checkIsOpenInvitedModal() {
    // Check if we should suppress the initial modal (coming from automatic employee creation)
    if (!this.hasCheckedInitialModal) {
      const suppressInviteModal = this.route.snapshot.queryParams['suppressInviteModal'];
      if (suppressInviteModal === 'true' || this.activationJourney.shouldSuppressInviteFocusCoach()) {
        this.hasCheckedInitialModal = true;
        return; // Don't show modal on initial load when suppressed
      }
    }
    if (this.activationJourney.shouldSuppressInviteFocusCoach()) {
      return;
    }
    this.hasCheckedInitialModal = true;

    if (this.usersLists && this.usersLists.length > 0 && !this.expired) {
      const invitedUser = this.usersLists.filter(user => !user.status || user.status == 'not-using');
      if (invitedUser && invitedUser.length == this.usersLists.length) {
        const milestoneReached = this.usersLists.some(user => (user.meta && user.meta.last_screenshot) || (user.meta && user.meta.last_application_name));
        let checkIfAnyUserLoginOrNot = this.usersLists.filter(user => (user.appInfo && user.appInfo.applicationType));
        if (!milestoneReached && checkIfAnyUserLoginOrNot.length == 0) {
          this.showFocusPopup();
        } else {
          this.isOpenFirstTimeModal = true;
        }
      }
    } else if (this.usersLists && this.usersLists.length == 0) {
      this.isOpenFirstTimeModal = true;
    }
  }

  setSubjects() {
    this.triggerInviteFocusModalOpen = this._sharedService.triggerInviteFocusModalOpen.subscribe((res) => {
      if (res) {
        if (!this.isOpenFirstTimeModal) {
          this.checkIsOpenInvitedModal();
        }
      }
    })

    this.unobserveInviteBadge = this._sharedService.unobserveInviteBadge.subscribe((response) => {
      if (this.invitedBadgeObserver) {
        if (this.invitedBadgeObserver && this.invitedBadgeRef?.nativeElement) {
          this.invitedBadgeObserver.unobserve(this.invitedBadgeRef.nativeElement);
        }
        this.invitedBadgeObserver.disconnect();
      }
      clearInterval(this.positionInterval);
    })
  }

  getTooltip(label: string, settings?: any, globalSettings?: any) {
    if (label && label == 'G' && settings && settings.ss_personalizeSettings && settings.ss_personalizeSettings == 'true') {
      return (settings && settings.ss_automatic_tracking && settings.ss_automatic_tracking === 'true') ? 'Admin Controlled' : 'Employee Controlled';
    } else if (label && label == 'G' && settings && settings.ss_personalizeSettings && settings.ss_personalizeSettings == 'false') {
      return (globalSettings && globalSettings.ss_automatic_tracking && globalSettings.ss_automatic_tracking === 'true') ? 'Admin Controlled' : 'Employee Controlled';
    } else if (label && label == 'S') {
      return 'Hidden';
    } else {
      return 'N/A';
    }
  }

  getOSInfo(osInfo) {

    const { os: platform, release } = osInfo;

    if (platform === 'win32') {
      const build = parseInt(release.split('.')[2], 10);
      if (release.startsWith('6.1')) return 'Windows 7';
      if (release.startsWith('6.2')) return 'Windows 8';
      if (release.startsWith('6.3')) return 'Windows 8.1';
      if (release.startsWith('10.0')) {
        return build >= 22000 ? 'Windows 11' : 'Windows 10';
      }
      return 'Unknown Windows version';
    }

    if (platform === 'darwin') {
      const darwinMajor = parseInt(release.split('.')[0], 10);
      const macOsRelease: any = this.sysConfig.macOsRelease
      const macOSVersionMap = new Map(macOsRelease);
      return macOSVersionMap.get(darwinMajor) || `Unknown macOS (Darwin ${darwinMajor})`;
    }

    if (platform === 'linux') {
      return 'Ubuntu';
    }

    return 'Unknown OS';
  }

  isOutdatedVersion(user: any): boolean {
    if (!user?.appInfo?.version) {
      return false; // No version info available, don't show warning
    }
    const versionState = this._projectService.getVersionState(user.appInfo.version, user?.osInfo);
    return versionState === this._projectService.VERSION_CASES.CRITICAL
      || versionState === this._projectService.VERSION_CASES.WARNING
      || versionState === this._projectService.VERSION_CASES.OUTDATED;
  }

  isCriticallyOutdated(user: any): boolean {
    if (!user?.appInfo?.version) {
      return false;
    }
    return this._projectService.getVersionState(user.appInfo.version, user?.osInfo)
      === this._projectService.VERSION_CASES.CRITICAL;
  }

  isWarningVersion(user: any): boolean {
    if (!user?.appInfo?.version) {
      return false;
    }
    return this._projectService.getVersionState(user.appInfo.version, user?.osInfo)
      === this._projectService.VERSION_CASES.WARNING;
  }

  getVersionWarningTooltip(user: any): string {
    const currentVersion = user?.appInfo?.version || 'Unknown';
    const versionTargets = this._projectService.getVersionTargetsByOs(user?.osInfo);
    return `Please update to v-${versionTargets.latest}. You are using v-${currentVersion} which is outdated.`;
  }

  hasVersionUpdate(user: any): boolean {
    return this.isOutdatedVersion(user);
  }

  /**
   * Filters users by selected statuses. If 'all' is selected or nothing is selected, show all users.
   * Otherwise, filter by selected statuses using the existing logic.
   */

}